import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNotifications } from "../../context/Notifications";
import SubmitButton from "../submitButton/SubmitButton";
import {
  updateCustomerCategoryService,
  createCustomerCategoryService,
} from "../../services/categories/services";
import { useTranslator } from "../../context/translator";
import { useGetUserByIdQuery } from "store/api/apiSlice";

function ClientCategoryModal({ category, text, getCategories, cssButton }) {
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const { addNotification } = useNotifications();
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [categoryData, setCategoryData] = useState(category);
  const { translate } = useTranslator();

  const toggleModal = () => {
    setIsLoading(false);
    setModalOpen(!modalOpen);
  };

  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;

    setCategoryData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    if (category._id === "new") {
      const body = {
        name: categoryData.name,
        active: true,
        companyId: userProfile?.user.companyId,
        isDisabledEmail: categoryData.isDisabledEmail || false,
        isDisabledPhoneNumber: categoryData.isDisabledPhoneNumber || false,
        isSpecialCommunication: categoryData.isSpecialCommunication || false,
        userId: userProfile?.user._id,
        ext: userProfile?.user.ext,
      };

      const response = await createCustomerCategoryService({ data: body });

      if (response?.success) {
        addNotification({
          id: Date.now(),
          message: translate("category-created"),
          kind: "success",
        });

        setCategoryData({
          _id: "new",
          key: "",
          name: "",
          counter: 0,
          active: true,
          isDisabledEmail: false,
          isDisabledPhoneNumber: false,
          isSpecialCommunication: false,
        });

        await getCategories();
        toggleModal();
      } else {
        addNotification({
          id: Date.now(),
          message: translate("error-to-create-category"),
          kind: "danger",
        });
      }
    } else {
      const response = await updateCustomerCategoryService({
        categoryId: categoryData._id,
        updateFields: {
          ...categoryData,
          userId: userProfile?.user?._id || user._id,
          ext: userProfile?.user?.ext || user.ext,
        },
      });

      if (response?.success) {
        addNotification({
          id: Date.now(),
          message: translate("category-updated"),
          kind: "success",
        });
        await getCategories();
        toggleModal();
      } else {
        addNotification({
          id: Date.now(),
          message: translate("error-to-update-category"),
          kind: "danger",
        });
      }
    }

    setIsLoading(false);
  };

  if (userLoading) return null;

  return (
    <>
      <button className={cssButton} onClick={toggleModal}>
        {text}
      </button>

      {modalOpen && (
        <div className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50">
          <div className="relative p-4 w-full max-w-2xl">
            <div className="bg-white rounded-lg dark:bg-gray-700 shadow">
              <div className="flex justify-between p-4 border-b dark:border-gray-600">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {category._id === "new"
                    ? translate("create-category")
                    : translate("update-category")}
                </h3>

                <button
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-black dark:text-white"
                  onClick={toggleModal}
                  disabled={isLoading}
                >
                  ✕
                </button>
              </div>

              <form className="p-4" onSubmit={handleSubmit}>
                <div className="mb-6 flex flex-col justify-start items-start">
                  <label className="label">{translate("name")}</label>
                  <input
                    type="text"
                    name="name"
                    required
                    defaultValue={categoryData.name}
                    onChange={handleChange}
                    className="input"
                  />
                </div>

                {category._id !== "new" && (
                  <div className="mb-6 flex flex-col justify-start items-start">
                    <label className="label justify-start">
                      {translate("status")}
                    </label>
                    <select
                      name="active"
                      defaultValue={category.active}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value={true}>{translate("active")}</option>
                      <option value={false}>{translate("deactivate")}</option>
                    </select>
                  </div>
                )}

                {/* CHECKBOXES */}
                <div className="mb-6 flex flex-row gap-2 justify-start items-start">
                  <label className="flex items-center gap-2 text-black dark:text-white">
                    <input
                      type="checkbox"
                      name="isDisabledEmail"
                      checked={categoryData.isDisabledEmail || false}
                      onChange={handleChange}
                    />
                    {translate("disable-email")}
                  </label>

                  <label className="flex items-center gap-2 text-black dark:text-white">
                    <input
                      type="checkbox"
                      name="isDisabledPhoneNumber"
                      checked={categoryData.isDisabledPhoneNumber || false}
                      onChange={handleChange}
                    />
                    {translate("disable-phone")}
                  </label>

                  <label className="flex items-center gap-2 text-black dark:text-white">
                    <input
                      type="checkbox"
                      name="isSpecialCommunication"
                      checked={categoryData.isSpecialCommunication || false}
                      onChange={handleChange}
                    />
                    {translate("special-communication")}
                  </label>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={toggleModal}
                    disabled={isLoading}
                    className="px-5 py-2.5 border rounded-lg text-black dark:text-white"
                  >
                    {translate("cancel")}
                  </button>

                  <SubmitButton
                    text={
                      category._id === "new"
                        ? translate("create-category")
                        : translate("update-category")
                    }
                    textLoading={translate("saving")}
                    isLoading={isLoading}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ClientCategoryModal;
